import React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { Check, AlertCircle } from 'lucide-react';
import { useSeo, Reveal, Laptop, DemoButton, Eyebrow, H2 } from './site/ui';
import { FlowDiagram } from './site/Diagrams';
import { PricingTeaser, Closing } from './site/HomeSections2';
import { segmentBySlug } from './site/content';

const SolutionHero = ({ s }) => (
  <section className="relative overflow-hidden pt-16" data-testid="solution-hero">
    <img src={s.image} alt={s.tile} className="absolute inset-0 h-full w-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#0b1220]/95 via-[#0b1220]/75 to-[#0b1220]/30" />
    <div className="relative mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
      <Reveal className="max-w-2xl">
        <Eyebrow dark>{s.tile}</Eyebrow>
        <h1 className="dl-display mt-4 text-4xl font-semibold leading-[1.04] tracking-tight text-[#f8fafc] sm:text-5xl lg:text-6xl" data-testid="solution-title">{s.headline}</h1>
        <p className="mt-5 text-base text-[#cbd5e1] md:text-lg">{s.sub}</p>
        <DemoButton dark className="mt-8" testid="solution-hero-book-demo" />
      </Reveal>
    </div>
  </section>
);

const SolutionPains = ({ s }) => (
  <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8" data-testid="solution-pains">
    <Eyebrow>The three big headaches</Eyebrow>
    <div className="mt-6 grid gap-4 md:grid-cols-3">
      {s.pains.map((p, i) => (
        <Reveal key={p} delay={i * 90} className="rounded-3xl bg-[#ffffff] p-6">
          <AlertCircle className="text-[#b91c1c]" size={22} />
          <p className="dl-display mt-4 text-xl font-semibold leading-snug text-[#0f172a]">{p}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

const SolutionFeatures = ({ s }) => (
  <section className="bg-[#efece4] py-20 sm:py-28" data-testid="solution-features">
    <div className="mx-auto max-w-7xl space-y-20 px-5 sm:px-8">
      {s.features.map((f, i) => (
        <div key={f.title} className={`grid items-center gap-10 lg:grid-cols-2 ${i % 2 ? 'lg:[&>*:first-child]:order-2' : ''}`}>
          <Reveal><Laptop src={f.screen} alt={f.title} /></Reveal>
          <Reveal delay={120}>
            <h3 className="dl-display text-3xl font-semibold text-[#0f172a]">{f.title}</h3>
            <p className="mt-4 flex gap-2 text-[15px] text-[#334155]"><Check size={18} className="mt-0.5 shrink-0 text-[#0f766e]" />{f.line}</p>
          </Reveal>
        </div>
      ))}
    </div>
  </section>
);

const DayTimeline = ({ s }) => (
  <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28" data-testid="solution-day">
    <div className="grid gap-12 lg:grid-cols-2">
      <div>
        <Eyebrow>How it connects</Eyebrow>
        <H2>One flow, start to finish.</H2>
        <div className="mt-10 rounded-3xl bg-[#ffffff] p-6"><FlowDiagram steps={s.flow} testid="solution-flow" /></div>
      </div>
      <div>
        <Eyebrow>A typical day with Driveline</Eyebrow>
        <ol className="mt-6 space-y-5 border-l-2 border-[#0f766e]/30 pl-6">
          {s.day.map(([t, what], i) => (
            <Reveal as="li" key={t} delay={i * 90} className="relative">
              <span className="absolute -left-[33px] top-1 h-4 w-4 rounded-full border-4 border-[#f7f5f0] bg-[#0f766e]" />
              <p className="text-sm font-bold text-[#0f766e]">{t}</p>
              <p className="mt-1 text-[15px] text-[#334155]">{what}</p>
            </Reveal>
          ))}
        </ol>
      </div>
    </div>
  </section>
);

export default function SolutionPage() {
  const { slug } = useParams();
  const s = segmentBySlug(slug);
  useSeo(s ? { ...s.seo, image: s.image } : { title: 'Driveline', description: '' });
  if (!s) return <Navigate to="/driveline" replace />;
  return (
    <div data-testid={`solution-page-${s.slug}`}>
      <SolutionHero s={s} />
      <SolutionPains s={s} />
      <SolutionFeatures s={s} />
      <DayTimeline s={s} />
      <div className="mx-auto max-w-7xl px-5 sm:px-8"><p className="mb-[-3rem] text-sm font-semibold text-[#0f766e]" data-testid="solution-plan">Most of them start on {s.plan}.</p></div>
      <PricingTeaser />
      <div className="pt-20"><Closing title={`See Driveline on your ${s.singular}’s numbers.`} /></div>
    </div>
  );
}
